import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import useAuth from '../../utilities/useAuth';
import useUserStore from '../../store/UserStore';
import localStorageStore from '../../store/localStorageStore';
import { useIsViewing } from '../../utilities/customHooks/useIsViewing';
import { useLogout } from '../../utilities/customHooks/useLogout';
import { trackEvent } from '../../modules/analytics/trackingMiddleware';
import { VIEWAS_STOP_VIEWING } from '../../modules/analytics/events';
import { useDashboardStore } from '../../store/DashboardStore';
import { useUnifiedDashboardStore } from '../../store/UnifiedDashboardStore';

import { PROFILE_MENU_ROUTES } from './header.constants';
import { visibleProfileMenuItems, type ProfileMenuItemId } from './header.rules';
import { announceViewAsChange, restoreViewAsSnapshot } from './viewAsSession';

export type ProfileDialog = 'viewAs' | 'checkout';

type ProfileOverlay =
  | { kind: 'closed' }
  | { kind: 'menu'; anchor: HTMLElement }
  | { kind: 'dialog'; dialog: ProfileDialog };

const CLOSED: ProfileOverlay = { kind: 'closed' };

export interface ProfileMenuItem {
  id: ProfileMenuItemId;
  onSelect: () => void;
}

export interface ProfileMenuLogic {
  userName: string;
  menuAnchor: HTMLElement | null;
  dialog: ProfileDialog | null;
  items: readonly ProfileMenuItem[];
  openMenu: (anchor: HTMLElement) => void;
  close: () => void;
  closeViewAs: () => void;
}

const useStopViewing = (): (() => void) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return () => {
    queryClient.clear();
    useUnifiedDashboardStore.getState().resetUnifiedDashboardStore();
    useDashboardStore.getState().resetDashboardStore();
    const { landingPage } = restoreViewAsSnapshot(localStorageStore);
    announceViewAsChange();
    trackEvent(VIEWAS_STOP_VIEWING, { targetUserId: localStorageStore.getItem('userId') || '' });
    navigate(landingPage ?? '/');
  };
};

export const useProfileMenu = (): ProfileMenuLogic => {
  const navigate = useNavigate();
  const logout = useLogout();
  const stopViewing = useStopViewing();
  const { role, isStaff } = useAuth();
  const { userName, allowAction } = useUserStore();
  const isViewing = useIsViewing();
  const [overlay, setOverlay] = useState<ProfileOverlay>(CLOSED);

  const close = () => setOverlay(CLOSED);
  const closeThen = (action: () => void) => () => {
    close();
    action();
  };
  const goTo = (to: string) => closeThen(() => navigate(to));
  const openDialog = (dialog: ProfileDialog) => () => setOverlay({ kind: 'dialog', dialog });
  const closeViewAs = () => {
    close();
    queueMicrotask(announceViewAsChange);
  };

  const handlers: Record<ProfileMenuItemId, () => void> = {
    profile: goTo(PROFILE_MENU_ROUTES.profile),
    notifications: goTo(PROFILE_MENU_ROUTES.notifications),
    logout: closeThen(logout),
    stopViewAs: closeThen(stopViewing),
    viewAs: openDialog('viewAs'),
    checkin: goTo(PROFILE_MENU_ROUTES.checkin),
    checkout: openDialog('checkout'),
    attendance: goTo(PROFILE_MENU_ROUTES.attendance),
    about: goTo(PROFILE_MENU_ROUTES.about),
  };

  const items = visibleProfileMenuItems({ role, isStaff, isViewing, allowAction }).map((id) => ({
    id,
    onSelect: handlers[id],
  }));

  return {
    userName,
    menuAnchor: overlay.kind === 'menu' ? overlay.anchor : null,
    dialog: overlay.kind === 'dialog' ? overlay.dialog : null,
    items,
    openMenu: (anchor) => setOverlay({ kind: 'menu', anchor }),
    close,
    closeViewAs,
  };
};
